import { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Database, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    useGetCustomApisQuery,
    useDeleteCustomApiMutation,
    CustomApiData,
} from '../../features/integrations/customApiApi';
import { CustomApiFormModal } from './components/CustomApiFormModal';

export default function IntegrationsPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedApi, setSelectedApi] = useState<CustomApiData | undefined>(undefined);

    const { data, isLoading, refetch } = useGetCustomApisQuery();
    const [deleteCustomApi, { isLoading: isDeleting }] = useDeleteCustomApiMutation();

    const customApis = data?.data || [];

    const handleEdit = (api: CustomApiData) => {
        setSelectedApi(api);
        setIsModalOpen(true);
    };

    const handleNew = () => {
        setSelectedApi(undefined);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete the "${name}" integration?`)) {
            try {
                await deleteCustomApi(id).unwrap();
                toast.success('Integration deleted successfully');
            } catch (err: any) {
                console.error(err);
                toast.error(err?.data?.message || 'Failed to delete integration');
            }
        }
    };

    // Filter APIs
    const filteredApis = customApis.filter((api) =>
        api.name_custom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        api.api_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        api.api_type.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'weather':
                return 'bg-blue-50 text-blue-700 border-blue-100';
            case 'prayer':
                return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'forex':
                return 'bg-amber-50 text-amber-700 border-amber-100';
            default:
                return 'bg-purple-50 text-purple-700 border-purple-100';
        }
    };

    return (
        <>
            <CustomApiFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialData={selectedApi}
            />

            <div className="flex-1 overflow-y-auto w-full custom-scrollbar pb-10 px-6 pt-6">
                {/* Header Card */}
                <div className="bg-gradient-to-r from-[#E9F7EF] via-white to-[#F1FAF5] border border-[#e4e7ec] rounded-[12px] p-6 relative overflow-hidden mb-6 shadow-sm">
                    <div className="absolute right-[-40px] top-[-40px] w-[180px] h-[180px] rounded-full bg-[#30A84B]/5 blur-2xl"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="bg-[#30A84B]/10 p-3.5 rounded-[12px] text-[#30A84B] border border-[#30A84B]/20">
                            <Database className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-[#101928]">Custom API Integrations</h1>
                            <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                Connect external real-time data sources (Weather, Prayer, Forex feeds) to dynamically map variables onto visual template layers.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                    {/* Search Bar */}
                    <div className="bg-white h-[44px] relative rounded-[8px] w-full sm:w-[380px] border border-[#e4e7ec] flex items-center px-3 hover:border-gray-300 focus-within:border-[#30A84B] focus-within:ring-2 focus-within:ring-[#30A84B]/15 transition-all shadow-sm">
                        <Search className="w-4 h-4 text-gray-400 shrink-0" />
                        <input
                            type="text"
                            placeholder="Search integrations by name or URL..."
                            className="font-sans font-normal text-sm w-full text-[#101928] bg-transparent outline-none px-2.5 py-1.5"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                        <button
                            onClick={() => refetch()}
                            className="p-2.5 bg-white border border-[#e4e7ec] text-gray-600 rounded-[8px] hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm cursor-pointer"
                            title="Refresh data"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                            onClick={handleNew}
                            className="bg-[#30a84b] hover:bg-[#288e3e] text-white px-4 py-2.5 rounded-[8px] flex items-center gap-2 font-medium text-sm transition-colors shadow-md cursor-pointer select-none"
                        >
                            <Plus className="w-4 h-4" />
                            New Integration
                        </button>
                    </div>
                </div>

                {/* Main Content Grid */}
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-white h-[200px] rounded-[12px] border border-gray-100 animate-pulse p-6 flex flex-col justify-between">
                            <div>
                                <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
                                <div className="h-4 w-48 bg-gray-150 rounded"></div>
                            </div>
                            <div className="h-10 w-full bg-gray-100 rounded-md"></div>
                          </div>
                        ))}
                    </div>
                ) : filteredApis.length === 0 ? (
                    <div className="w-full flex flex-col items-center justify-center min-h-[300px] bg-white rounded-[12px] border border-dashed border-gray-200 p-8 shadow-sm">
                        <div className="bg-gray-50 p-4 rounded-full text-gray-400 mb-4">
                            <Database className="w-8 h-8" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-700">No Integrations Found</h3>
                        <p className="text-sm text-gray-500 mt-1 text-center max-w-sm">
                            {searchQuery ? 'No integrations match your search criteria. Try a different term.' : 'Connect your first external API to get started mapping data to your template designs.'}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={handleNew}
                                className="mt-4 text-[#30a84b] font-semibold text-sm hover:underline cursor-pointer flex items-center gap-1.5"
                            >
                                <Plus className="w-4 h-4" /> Connect API
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredApis.map((api) => (
                            <div
                                key={api._id}
                                className="bg-white border border-[#e4e7ec] rounded-[12px] p-6 hover:shadow-lg hover:border-gray-300 transition-all duration-300 flex flex-col justify-between min-h-[220px] group relative"
                            >
                                <div>
                                    {/* Row 1: Name and Context Badge */}
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <h3 className="font-bold text-base text-[#101928] line-clamp-1 group-hover:text-[#30A84B] transition-colors" title={api.name_custom}>
                                            {api.name_custom}
                                        </h3>
                                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border tracking-wide uppercase shrink-0 ${getTypeColor(api.api_type)}`}>
                                            {api.api_type}
                                        </span>
                                    </div>

                                    {/* Row 2: Method and URL */}
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="px-1.5 py-0.5 font-mono text-[10px] font-bold bg-slate-100 text-slate-700 rounded border border-slate-200">
                                            {api.method}
                                        </span>
                                        <span className="font-mono text-xs text-gray-500 truncate select-all" title={api.api_url}>
                                            {api.api_url}
                                        </span>
                                    </div>

                                    {/* Row 3: Meta info */}
                                    <div className="grid grid-cols-2 gap-3 text-[11px] text-gray-500 mb-6 bg-slate-50 p-2.5 rounded-[8px] border border-gray-100">
                                        <div>
                                            <span className="block text-gray-400">Sync Frequency:</span>
                                            <span className="font-medium text-gray-700">Every {api.refresh_interval || 30} mins</span>
                                        </div>
                                        <div>
                                            <span className="block text-gray-400">Cached Fields:</span>
                                            <span className="font-medium text-gray-700">
                                                {api.customapidata ? `${Object.keys(api.customapidata).length} keys` : 'No payload'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Row 4: Actions */}
                                <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto">
                                    <span className="text-[10px] text-gray-400">
                                        {api.last_refreshed_at ? (
                                            <>Synced: {new Date(api.last_refreshed_at).toLocaleTimeString()}</>
                                        ) : (
                                            <span className="flex items-center gap-1 text-amber-500">
                                                <AlertCircle className="w-3 h-3" /> Needs configuration test
                                            </span>
                                        )}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(api)}
                                            className="p-1.5 text-gray-500 hover:text-[#30A84B] hover:bg-gray-50 rounded-md transition-all cursor-pointer"
                                            title="Edit configuration"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => api._id && handleDelete(api._id, api.name_custom)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-all cursor-pointer"
                                            title="Delete integration"
                                            disabled={isDeleting}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
